// src/pages/Settings.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const APP_VERSION = "V1.0.25";

const Settings = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState(true);
  const [emailUpdates, setEmailUpdates] = useState(true);

  // Debugging
  useEffect(() => {
    console.log('🔍 User object in Settings:', user);
  }, [user]);

  const formatAccountCreation = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Unknown';
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
      return date.toLocaleDateString('en-KE', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return 'Unknown';
    }
  };

  const getDetailedCreationInfo = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Unknown';
      return date.toLocaleDateString('en-KE', {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return 'Unknown';
    }
  };

  return (
    <div className="min-h-screen bg-custom-light-gray py-8 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-4xl mx-auto mt-14">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-heading text-primary">Settings</h1>
          <p className="text-gray-500 text-[14px] font-subheading mt-2">
            Manage your account preferences and application settings
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Account Information */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-[14px] font-subheading text-dark mb-4">Account Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-subheading text-dark mb-1">Full Name</label>
                <p className="text-dark bg-custom-light-gray px-3 py-2 rounded border border-gray-200 font-sans">
                  {user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user?.firstName || user?.lastName || 'Not provided'}
                </p>
              </div>
              <div>
                <label className="block text-xs font-subheading text-dark mb-1">Business</label>
                <p className="text-dark bg-custom-light-gray px-3 py-2 rounded border border-gray-200 font-sans">
                  {user?.business || 'Not provided'}
                </p>
              </div>
              <div>
                <label className="block text-xs font-subheading text-dark mb-1">Email</label>
                <p className="text-dark bg-custom-light-gray px-3 py-2 rounded border border-gray-200 font-sans">
                  {user?.email || 'Not provided'}
                </p>
              </div>
              <div>
                <label className="block text-xs font-subheading text-dark mb-1">User Type</label>
                <p className="text-dark bg-custom-light-gray px-3 py-2 rounded border border-gray-200 font-sans capitalize">
                  {user?.role || 'User'}
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-subheading text-dark mb-1">Account Created</label>
                <div className="bg-custom-light-gray px-3 py-2 rounded border border-gray-200">
                  <p className="text-xs text-dark font-medium">{formatAccountCreation(user?.createdAt)}</p>
                  <p className="text-xs text-gray-500 mt-1">{getDetailedCreationInfo(user?.createdAt)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Notification Preferences */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-[14px] font-subheading text-dark mb-4">Notification Preferences</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-subheading text-dark">Push Notifications</p>
                  <p className="text-sm text-gray-500">Receive alerts for new orders and matches</p>
                </div>
                <button
                  onClick={() => setNotifications(!notifications)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors font-accent ${notifications ? 'bg-primary' : 'bg-gray-200'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notifications ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-subheading text-dark">Email Updates</p>
                  <p className="text-sm text-gray-500">Receive weekly summary emails</p>
                </div>
                <button
                  onClick={() => setEmailUpdates(!emailUpdates)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors font-accent ${emailUpdates ? 'bg-primary' : 'bg-gray-200'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${emailUpdates ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* App Preferences */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-[14px] font-subheading text-dark mb-4">Application Preferences</h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm font-subheading text-dark">Theme</p>
                <p className="text-sm text-gray-500">Light mode (Dark mode coming soon)</p>
              </div>
              <div>
                <p className="text-sm font-subheading text-dark">Language</p>
                <p className="text-sm text-gray-500">English</p>
              </div>
              <div>
                <p className="text-sm font-subheading text-dark">Auto-refresh</p>
                <p className="text-sm text-gray-500">Every 15 seconds (for real-time updates)</p>
              </div>
            </div>
          </div>

          {/* Support & Information */}
          <div className="p-6">
            <h2 className="text-[14px] font-subheading text-dark mb-4">Support & Information</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-brand-orange/10 rounded-lg border border-brand-orange">
                <div>
                  <p className="text-[12px] text-accent">App Version</p>
                  <span className="text-[12px] text-accent font-subheading">Version: {APP_VERSION} (MVP)</span>
                </div>
              </div>

              <div className="text-center pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 font-sans">
                  © {new Date().getFullYear()} Climatech. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
